import { DocumentDropZone } from "./DocumentDropZone";
import { useState } from "react";
import { instance } from "../../../../../../api/apiBase";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { Button, ButtonSize, ButtonVariant } from "shared/components";
import { getErrorMessage } from "../../../../../../utils/error";
import { FilePreview } from "./FileReview";
import { Show } from "shared/components";
import { uploadFileToS3Bucket } from "../../../../utils/uploadFileToS3Bucket";
import { documentValidations } from "../../validation";
import { DOC_TYPE_ERROR_MESSAGE } from "../../constants";
import { UploadedDocumentData, UploadedFile } from "../../types";

export const DocumentUploadView = ({
  applicantId
}: {
  applicantId: number;
}) => {
  const queryClient = useQueryClient();
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const { mutate } = useMutation({
    mutationKey: ["ADDITIONAL_DOCUMENTS", applicantId],
    mutationFn: (documents: UploadedDocumentData[]) =>
      instance.post(
        `/portal/v1/applicants/${applicantId}/additional_documents/bulk_create`,
        {
          documents
        }
      ),
    onError(error) {
      toast.error(getErrorMessage(error) || "Something went wrong");
    },
    onSuccess: () => {
      setFiles([]);
      toast.success(
        "Success! Additional documents were successfully uploaded."
      );
      queryClient.invalidateQueries({
        queryKey: ["ADDITIONAL_DOCUMENTS", applicantId]
      });
    }
  });

  const getPresignedUrl = async (file: File) => {
    const queryParams = new URLSearchParams({
      filename: file.name,
      type: file.type
    }).toString();

    const { data } = await instance(`/portal/v1/assets/presign?${queryParams}`);
    return data.url;
  };

  const handleUpload = async () => {
    try {
      setUploading(true);
      setErrorMessage(null);
      const links = await Promise.all(
        files.map(async ({ file, type }) => {
          await documentValidations.validate({ file, type });
          const presignedUrl = await getPresignedUrl(file);
          const { url } = await uploadFileToS3Bucket(presignedUrl, file);
          return {
            document_type: type as string,
            url,
            metadata: {
              size: file.size,
              original_filename: file.name,
              content_type: file.type
            }
          };
        })
      );
      mutate(links);
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      if (errorMessage === DOC_TYPE_ERROR_MESSAGE)
        setErrorMessage(errorMessage);
      else toast.error(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-4">
      <DocumentDropZone files={files} setFiles={setFiles} />

      <Show if={Boolean(files.length)}>
        <div className="space-y-4">
          {files.map((doc, idx) => (
            <FilePreview
              isTypeError={Boolean(errorMessage) && !doc.type}
              key={idx}
              file={doc.file}
              onDelete={() =>
                setFiles(files.filter((_, index) => idx !== index))
              }
              onSelectType={(type) => {
                const newDocuments = [...files];
                newDocuments[idx].type = type;
                setFiles(newDocuments);
              }}
            />
          ))}
        </div>

        <p className="text-end font-bold text-sm text-danger-500">
          {errorMessage}
        </p>

        <div className="flex justify-end gap-2">
          <Button
            onClick={() => setFiles([])}
            className="w-[103px]"
            size={ButtonSize.SM}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            className="w-[103px]"
            loading={uploading}
            size={ButtonSize.SM}
            variant={ButtonVariant.PRIMARY}
          >
            Upload
          </Button>
        </div>
      </Show>
    </div>
  );
};
